/** "What have you been doing?" prompt shown on Change task / Clock out — common task chips + add-new + optional note. */
import React, { useState } from 'react';
import { Check, Loader2, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { fmtHours, textOn } from './utils';
import { PhotoAttach } from './PhotoAttach';

const hhmm = (iso) => new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });

export function TaskPrompt({ open, mode, timer, jobTypes, onSubmit, onClose }) {
  const [picked, setPicked] = useState(null);
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [breakLen, setBreakLen] = useState(30);
  const [photos, setPhotos] = useState([]);
  if (!open || !timer) return null;
  const mins = Math.max(1, Math.floor((Date.now() - new Date(timer.started_at).getTime()) / 60000));
  const reset = () => { setPicked(null); setAdding(false); setNewName(''); setNote(''); setPhotos([]); };
  const submit = async () => {
    setBusy(true);
    try { await onSubmit({ job_type_id: adding ? null : picked, new_task_name: adding ? newName.trim() : null, description: note.trim(), photo_ids: photos, break_minutes: mode === 'break' ? breakLen : undefined }); reset(); } finally { setBusy(false); }
  };
  const ready = adding ? newName.trim().length > 0 : !!picked;
  return (
    <Dialog open onOpenChange={(o) => { if (!o) { reset(); onClose(); } }}>
      <DialogContent className="sm:max-w-lg max-h-[92dvh] overflow-y-auto overscroll-contain p-0 gap-0" data-testid="task-prompt">
        <div className="p-5 space-y-4">
        <DialogHeader><DialogTitle>{mode === 'out' ? 'Before you clock out…' : mode === 'break' ? 'Taking a break' : 'Change task'}</DialogTitle></DialogHeader>
        <p className="text-sm text-gray-700" data-testid="task-prompt-question">What have you been doing since <b>{hhmm(timer.started_at)}</b> (<b>{fmtHours(mins)}</b>)?</p>
        <div className="flex flex-wrap gap-2" data-testid="task-prompt-chips">
          {jobTypes.filter((j) => j.active !== false).map((j) => {
            const on = picked === j.id && !adding;
            return (
            <button key={j.id} type="button" onClick={() => { setPicked(j.id); setAdding(false); }} data-testid={`task-chip-${j.id}`} aria-pressed={on}
              className={`px-3 py-1.5 rounded-full text-sm font-semibold border-2 inline-flex items-center gap-1 transition-[opacity,transform,box-shadow] ${on ? 'border-gray-900 ring-2 ring-offset-2 ring-gray-900 shadow-md scale-105' : 'border-transparent opacity-70 hover:opacity-100'}`}
              style={{ background: j.colour || '#475569', color: textOn(j.colour || '#475569') }}>{on && <Check className="w-4 h-4" />}{j.name}</button>
          ); })}
          <button type="button" onClick={() => { setAdding(true); setPicked(null); }} data-testid="task-chip-add" className={`px-3 py-1.5 rounded-full text-sm font-semibold border-2 border-dashed inline-flex items-center gap-1 ${adding ? 'border-gray-900 bg-gray-100' : 'border-gray-300 text-gray-600 hover:border-gray-500'}`}><Plus className="w-3.5 h-3.5" /> Add new task</button>
        </div>
        {adding && <Input autoFocus value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Name the task, e.g. Making Samples" maxLength={60} data-testid="task-prompt-new-name" />}
        {mode === 'break' && (
          <div data-testid="break-length">
            <p className="text-xs text-gray-600 mb-1.5">How long is your break? (we'll let the office know)</p>
            <div className="flex flex-wrap gap-2">{[15, 30, 45, 60].map((m) => <button key={m} type="button" onClick={() => setBreakLen(m)} data-testid={`break-length-${m}`} className={`px-3 py-1.5 rounded-full text-sm font-bold border-2 ${breakLen === m ? 'bg-amber-500 border-amber-500 text-white' : 'bg-white border-gray-200 text-gray-700 hover:border-amber-300'}`}>{m} min</button>)}</div>
          </div>
        )}
        <div>
          <p className="text-xs text-gray-600 mb-1.5">What did you get done? (optional)</p>
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Made 20 ProBound sample boards, packed the Leeds order and chased two quotes" maxLength={500} rows={4} className="min-h-[7rem] text-base resize-y" data-testid="task-prompt-note" />
          <div className="text-[11px] text-gray-400 text-right mt-1" data-testid="task-prompt-note-count">{note.length}/500</div>
        </div>
        <div>
          <p className="text-xs text-gray-600 mb-1.5">Add a photo of what you've done (optional)</p>
          <PhotoAttach photos={photos} onChange={setPhotos} disabled={busy} />
        </div>
        </div>
        <div className="sticky bottom-0 bg-white/95 backdrop-blur border-t p-3" data-testid="task-prompt-footer">
          {!ready && <p className="text-[11px] text-amber-700 text-center mb-1.5" data-testid="task-prompt-pick-hint">Tap a task above to continue</p>}
          <Button onClick={submit} disabled={!ready || busy} className="w-full h-11 bg-gray-900 text-white font-bold" data-testid="task-prompt-save">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : mode === 'out' ? `Save ${fmtHours(mins)} & clock out` : mode === 'break' ? `Save ${fmtHours(mins)} & start ${breakLen} min break` : `Save ${fmtHours(mins)} & start next task`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
